import {
  FormConfigurationFilterAreaSchema,
  FormConfigurationFilterPanel,
  type FormConfigurationFilterAreaType,
} from "#batteries-included-components/Layouts/RecordConfiguration/FormConfigurationFilterPanel";
import { FunctionSelector } from "#batteries-included-components/Layouts/RecordConfiguration/FunctionSelector";
import { RecordValueConfigurationContext } from "#batteries-included-components/Layouts/RecordConfiguration/RecordConfigurationContext";
import { DEFAULT_QUERY_OPTIONS } from "#hooks/adapters/adapterUtils";
import { useParams } from "#routers/hooks";
import { linkToFormSubmissionDetail } from "#routers/links";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import {
  useClientSideSortingAndPagination,
  useTableSortingAndPagination,
} from "#src/components/Redux/reducers/tableStateReducer";
import { useGetOneFormSchema } from "#src/components/hooks/adapters/useFormSchema";
import { useListFormSubmissions } from "#src/components/hooks/adapters/useFormSubmissions";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { linkToAssetDetailPage } from "#utils/links";
import {
  getAssetIdsByType,
  getSourceName,
  getSubjectIdFromFilters,
} from "#utils/recordUtils";
import { UseQueryOptions, useQueries, useQuery } from "@tanstack/react-query";
import {
  Column,
  DataTable,
  DataTablePanel,
  HeaderType,
  Row,
} from "@validereinc/common-components";
import {
  AssetType,
  AssetTypeType,
  EquipmentAdapter,
  EquipmentType,
  FacilityAdapter,
  FacilityType,
  FlowAdapter,
  FlowType,
  FormCategoryAdapter,
  FormResponseDataItemType,
  FormSchemaQuestionType,
  FormSubmissionAdapter,
  MeasurementsDomain,
  RecordValueConfigurationTypeType,
  Resources,
  SeriesResponseDataItemType,
  SortDirection,
  UserReturnType,
  UsersAdapter,
  isMeasurementFormQuestion,
  type FormSubmissionMeasurementQuestionAnswerType,
  type FormSubmissionType,
} from "@validereinc/domain";
import React, { useContext, useEffect, useMemo, useState } from "react";

const getQuestionByMeasurementType = (
  measurementType?: string,
  formSubmission?: FormSubmissionType
) => {
  if (!measurementType || !formSubmission) return;

  const relevantQuestion = Object.entries(
    formSubmission?.form_schema?.config.questions ?? {}
  ).find(
    ([_, question]: [string, FormSchemaQuestionType]) =>
      isMeasurementFormQuestion(question) &&
      question?.measurement_type === measurementType
  );

  if (!relevantQuestion) return;

  const [questionId, question] = relevantQuestion;

  /* Because there's no way to link the measurement to a specific question, we just grab the
     first one of the appropriate measurement type on the submission */
  const answer = Object.values(formSubmission.answers)
    .flat()
    .find(({ [questionId]: value }) => value)?.[questionId];

  return {
    question,
    questionId,
    answer: answer as FormSubmissionMeasurementQuestionAnswerType,
  };
};

type FormConfigurationItemType = FormResponseDataItemType & {
  questionId?: string;
  question?: FormSchemaQuestionType;
  answer?: FormSubmissionMeasurementQuestionAnswerType;
  form_submission?: FormSubmissionType;
  user?: UserReturnType;
  asset?: FacilityType | EquipmentType | FlowType;
};

export const FormConfiguration = () => {
  const params = useParams<{ measurementType: string }>();
  const [filterConfigStorageKey, tableConfigStorageKey, tableStateStorageKey] =
    useStorageKey(
      ["filter-configuration", "table-configuration", "table-state"],
      "record-source-configuration-panel-forms-tab"
    );
  const [storedFilters] = useStickyState<FormConfigurationFilterAreaType>(
    {},
    filterConfigStorageKey
  );
  const {
    assetType: subjectType,
    formSchema: formSchemaId,
    formCategory: formCategoryId,
  } = FormConfigurationFilterAreaSchema.safeParse(storedFilters).data ?? {};
  const subjectId = getSubjectIdFromFilters(
    storedFilters as Record<string, string>
  );

  const {
    measurementType,
    form,
    record,
    isLoading: isRecordCtxLoading,
  } = useContext(RecordValueConfigurationContext) ?? {};

  const [selected, setSelectedState] = useState<
    Record<string, SeriesResponseDataItemType>
  >({});

  const formCategoryQuery = useQuery({
    queryKey: [Resources.FORM_CATEGORY, formCategoryId],
    queryFn: () => FormCategoryAdapter.getOne({ id: formCategoryId ?? "" }),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: !!formCategoryId,
  });

  const formTemplateQuery = useGetOneFormSchema(
    { id: formSchemaId ?? "" },
    { enabled: !!formSchemaId }
  );

  const measurementPlotParams = {
    measurementType: params.measurementType,
    ...(subjectType && subjectId ? { subjectType, subjectId } : {}),
    start: storedFilters.dateRange?.from,
    end: storedFilters.dateRange?.to,
    include_forms: "true",
  };

  const isMeasurementPlotQueryEnabled =
    !!storedFilters.dateRange?.from && !!storedFilters.dateRange.to;
  const measurementPlotQuery = useQuery({
    queryKey: ["measurementsPlot", measurementPlotParams],
    queryFn: () =>
      MeasurementsDomain.getMeasurementPlotByMeasurementType(
        measurementPlotParams
      ),
    enabled: isMeasurementPlotQueryEnabled,
  });

  const formSubmissionIds = Array.from(
    new Set(
      measurementPlotQuery.data?.form?.data
        .filter(({ form_category: { id } }) =>
          formCategoryId ? id === formCategoryId : true
        )
        .map((item) => item.form_submission_id)
    )
  );
  const formSubmissionParams: Parameters<
    typeof FormSubmissionAdapter.getList
  >[0] = {
    filters: {
      id: formSubmissionIds,
    },
    meta: {
      answers: true,
    },
  };

  const isFormSubmissionQueryEnabled = !!formSubmissionIds.length;
  const formSubmissionQuery = useListFormSubmissions(formSubmissionParams, {
    enabled: isFormSubmissionQueryEnabled,
  });
  const formSubmissions = formSubmissionQuery.data?.data ?? [];

  const userQueries = useQueries<
    Array<
      UseQueryOptions<
        Awaited<ReturnType<typeof UsersAdapter.getOne>> | undefined,
        unknown,
        UserReturnType | undefined
      >
    >
  >({
    queries: Array.from(
      new Set(formSubmissions.map(({ created_by }) => created_by) ?? [])
    ).map((id) => ({
      queryKey: ["users", id],
      queryFn: () => {
        return UsersAdapter.getOne({ id: id ?? "" });
      },
      staleTime: 5 * 60 * 1000,
      select: (resp) => resp?.data,
    })),
  });

  const usersMap = useMemo(() => {
    return userQueries.reduce<Record<string, UserReturnType>>((map, q) => {
      if (!q.data?.id || map[q.data.id]) {
        return map;
      }

      map[q.data.id] = q.data;
      return map;
    }, {});
  }, [userQueries]);

  const measurements =
    measurementPlotQuery.data?.form?.data
      .filter(
        (item) =>
          (!formCategoryId || item.form_category.id === formCategoryId) &&
          (!formSchemaId || item.form_schema.id === formSchemaId)
      )
      .map((item) => {
        const form_submission = formSubmissions.find(
          ({ id }) => id === item.form_submission_id
        );
        const user = form_submission?.created_by
          ? usersMap[form_submission.created_by]
          : null;

        const details = getQuestionByMeasurementType(
          params.measurementType,
          form_submission
        );

        return {
          ...item,
          ...(details ?? {}),
          form_submission,
          ...(user ? { user } : {}),
        };
      }) ?? [];
  const assets = measurements.map(({ answer }) => ({
    id: answer?.subject_id,
    type: answer?.subject_type,
  }));

  const equipmentParams: Parameters<typeof EquipmentAdapter.getList>[0] = {
    // @ts-expect-error TODO: fix filter typing
    filters: { id: getAssetIdsByType(assets as any, AssetType.EQUIPMENT) },
  };
  const equipmentQuery = useQuery({
    queryKey: ["equipment", equipmentParams],
    queryFn: () => EquipmentAdapter.getList(equipmentParams as any),
    enabled: !!equipmentParams.filters?.id?.length,
    select: ({ data }) => data,
  });

  const facilityParams = {
    filters: { id: getAssetIdsByType(assets as any, AssetType.FACILITY) },
  };
  const facilityQuery = useQuery({
    queryKey: ["facilities", facilityParams],
    queryFn: () => FacilityAdapter.getList(facilityParams),
    enabled: !!facilityParams.filters.id.length,
    select: ({ data }) => data,
  });

  const flowParams: Parameters<typeof FlowAdapter.getList>[0] = {
    // @ts-expect-error TODO: fix filter typing
    filters: { id: getAssetIdsByType(assets as any, AssetType.FLOW) },
  };
  const flowQuery = useQuery({
    queryKey: ["flows", flowParams],
    queryFn: () => FlowAdapter.getList(flowParams),
    // @ts-expect-error TODO: fix filter typing
    enabled: !!flowParams.filters.id.length,
    select: ({ data }) => data,
  });

  const getAsset = (assetType: AssetTypeType, assetId: string) => {
    switch (assetType) {
      case AssetType.EQUIPMENT:
        return equipmentQuery.data?.find(({ id }) => assetId === id);
      case AssetType.FACILITY:
        return facilityQuery.data?.find(({ id }) => assetId === id);
      case AssetType.FLOW:
      default:
        return flowQuery.data?.find(({ id }) => assetId === id);
    }
  };

  const rawItems = measurements.map((item) => ({
    ...item,
    asset: getAsset(
      item.answer?.subject_type as AssetTypeType,
      item.answer?.subject_id ?? ""
    ),
  }));

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "form_submission.form_schema.name",
      sortDirection: SortDirection.ASCENDING,
    },
    undefined,
    tableStateStorageKey
  );
  const { items, pagination, sorting } = useClientSideSortingAndPagination(
    rawItems,
    tableState
  );

  const setSelected = (selectedItems: Record<string, any>) => {
    setSelectedState(selectedItems);
    form?.setValue("measurement_ids", Object.keys(selectedItems));
  };

  useEffect(() => {
    form?.setValue("form_category_id", formCategoryId ?? "");
  }, [formCategoryId]);

  useEffect(() => {
    if (measurementPlotQuery.isLoading) return;
    const initialSelection = Object.fromEntries(
      (
        record?.values.find((v) => v.measurement_type === measurementType?.id)
          ?.configuration.measurement_ids ?? []
      )
        .map((selectedId: string) => {
          const findFormSubmission = items.find(({ id }) => id === selectedId);
          return findFormSubmission ? [selectedId, findFormSubmission] : null;
        })
        .filter((e) => !!e)
    );
    setSelected(initialSelection);
  }, [
    record,
    measurementType,
    measurementPlotQuery.isLoading,
    formCategoryId,
    formSchemaId,
  ]);

  const formCategory = formCategoryQuery?.data;
  const isLoading =
    isRecordCtxLoading ||
    (isFormSubmissionQueryEnabled && measurementPlotQuery.isLoading) ||
    (formSubmissionIds.length && formSubmissionQuery.isLoading) ||
    userQueries.some((q) => q.isLoading) ||
    // @ts-expect-error TODO: fix filter typing
    (!!equipmentParams.filters.id.length && equipmentQuery.isLoading) ||
    (!!facilityParams.filters.id.length && facilityQuery.isLoading) ||
    (!!formCategoryId && formCategoryQuery.isLoading) ||
    // @ts-expect-error TODO: fix filter typing
    (!!flowParams.filters.id.length && flowQuery.isLoading);

  // REVIEW: FormConfigurationItemType doesn't seem to be what the items are
  const headers: Array<HeaderType<FormConfigurationItemType>> = [
    {
      key: "form_submission.form_schema.name",
      label: "Name",
      renderComponent: ({ item }) => (
        <RoutingLink to={linkToFormSubmissionDetail(item?.form_submission_id)}>
          {getSourceName(
            RecordValueConfigurationTypeType.MEASUREMENT,
            {
              form_schema_name: item?.form_schema?.name,
              id: item?.id,
              form_submission_id: item?.form_submission_id,
            },
            item
          )}
        </RoutingLink>
      ),
    },
    {
      key: "asset.name",
      label: "Asset",
      renderComponent: ({ item }) =>
        item.answer?.subject_type &&
        item.answer?.subject_id &&
        item.asset?.name ? (
          <RoutingLink
            to={linkToAssetDetailPage(
              item.answer?.subject_type as AssetTypeType,
              item.answer?.subject_id
            )}
          >
            {item.asset?.name}
          </RoutingLink>
        ) : (
          "-"
        ),
    },
    {
      key: `measure_value`,
      label: measurementType?.name,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.NumberCell value={item.measure_value} />
      ),
    },
    {
      key: "time",
      label: "Date",
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell value={item.time} />
      ),
    },
    {
      key: `form_submission.created_at`,
      label: "Submitted On",
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell value={item.form_submission?.created_at} />
      ),
    },
    {
      key: `user.name`,
      label: "Submitted By",
    },
  ];

  return (
    <>
      <FormConfigurationFilterPanel storageKey={filterConfigStorageKey} />
      <Row>
        <Column variant={18}>
          <DataTablePanel
            panelProps={{ title: "Form Submissions" }}
            dataTableProps={{
              headers: (headers ?? []) as Array<HeaderType<any>>,
              items,
              emptyStateProps: {
                title: formCategory
                  ? `There are no form submissions (category: ${formCategory.name}${formTemplateQuery.data?.data.name ? `, template: ${formTemplateQuery.data.data.name}` : ""}) with '${measurementType?.name}' measurements`
                  : "Select a Form Category",
                suggestion: formCategory
                  ? "Try adjusting the filters or searching under the other source types"
                  : "",
              },
              isLoading,
              getItemId: (item: any) => item.id,
              selected: items.length ? selected : undefined,
              onSelectionChange: (newSelection: Record<string, any>) => {
                setSelected(newSelection);
              },
              sorting,
              pagination,
              onSortChange: updateTableState,
              onPaginationChange: updateTableState,
            }}
            storageKey={tableConfigStorageKey}
          />
        </Column>
        <Column variant={6}>
          <FunctionSelector
            values={Object.values(selected).map(
              (item) => item.measure_value ?? 0
            )}
            isLoading={isLoading}
          />
        </Column>
      </Row>
    </>
  );
};
